<template>
  <div class="contact-page">
    <!-- Header with gradient, without double navigation -->
    <header class="header">
      <div class="header-content">
        <h1 class="main-title">Contact us</h1>
        <p class="subtitle">Our team is at your disposal to answer all your questions.</p>
      </div>
    </header>

    <div class="content-container">
      <!-- Introduction -->
      <section class="card intro-card">
        <div class="card-content">
          <h2 class="section-title">Send us a message !</h2>
          <div class="text-content">
            <p>
              If you would like to receive a quote for our services, please include as many details as possible in your message.
              This way, we can better understand your needs and offer you a solution tailored to your situation.
            </p>
            
            <p>
              Feel free to ask any additional questions you may have about our products,
              services, or any other inquiries. We are here to provide you with the information you need.
            </p>
            
            <p>
              We are also open to discussion ! If you simply want to exchange ideas, share your
              feedback, or discuss topics related to the digitalization of your business, we would be delighted to read your messages and engage in a conversation
              with you.
            </p>
          </div>
        </div>
      </section>
      
      <!-- Appointment -->
      <section class="card why-choose-card">
        <div class="card-content">
          <h2 class="section-title">Schedule a phone / video appointment</h2>
          <div class="cta-container">
            <span class="cta-button fake-link" @click="navigateTo('e1')">Your personalized appointment now</span>
          </div>
        </div>
      </section>
      
      <!-- Form -->
      <section class="card form-card">
        <div class="card-content">
          <h2 class="section-title">Or through the contact form below</h2>
          
          <form @submit.prevent="submitForm" class="contact-form">
            <div class="form-group">
              <label for="nom">Name</label>
              <input type="text" id="nom" v-model="formData.nom" placeholder="Your name *" required>
            </div>
            
            <div class="form-group">
              <label for="email">Email</label>
              <input type="email" id="email" v-model="formData.email" placeholder="Your email address *" required>
            </div>
            
            <div class="form-group">
              <label for="sujet">Subject</label>
              <input type="text" id="sujet" v-model="formData.sujet" placeholder="Subject *" required>
            </div>
            
            <div class="form-group">
              <label for="message">Message</label>
              <textarea id="message" v-model="formData.message" placeholder="Your message *" rows="6" required></textarea>
            </div>
            
            <div class="form-group">
              <PuzzleCaptcha 
                :puzzle-image="puzzleImage" 
                ref="puzzleCaptcha"
                @verification-change="onVerificationChange"
              />
            </div>
            
            <p class="form-note">* Required fields</p>
            <p v-if="successMessage" class="form-success">{{ successMessage }}</p>
            
            <div class="cta-container">
              <button type="submit" class="cta-button">Send</button>
            </div>
          </form>
          
          <p class="privacy-notice">
            By filling out this form, you agree that web-passion.fr will possess your email address and the
            information transmitted in this message during our future exchanges.
          </p>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { useHead } from '@vueuse/head'
import axios from 'axios'
import PuzzleCaptcha from '@/components/common/PuzzleCaptcha.vue'

export default {
  name: 'ContactView',
  components: {
    PuzzleCaptcha
  },
  setup() {
    useHead({
      title: 'Contact - Information request and quote | Arynae',
      meta: [
        { name: 'description', content: 'Contact Arynae to discuss your digital projects. Request a free quote for our SEO services, SaaS creation, chatbots or No Code solutions.' },
        { name: 'keywords', content: 'contact Arynae, quote request, digital agency, SEO contact, SaaS development contact, chatbot quote, no code quote' },
        { name: 'robots', content: 'index, nofollow' },
        { name: 'author', content: 'Arynae' },
        { 'http-equiv': 'Content-Type', content: 'text/html; charset=utf-8' },
        // Essential Open Graph
        { property: 'og:type', content: 'website' },
        { property: 'og:title', content: 'Contact - Information request and quote | Arynae' },
        { property: 'og:description', content: 'Contact Arynae to discuss your digital projects. Request a free quote for our SEO services, SaaS creation, chatbots or No Code solutions.' },
        { property: 'og:url', content: 'https://arynae.com/en/contact' },
        { property: 'og:locale', content: 'en_US' },
        { property: 'og:image', content:'https://arynae.com/img/arynae-social-card.png' },
        { property: 'og:image:alt', content:'Digital agency - No Code solutions, SaaS, Chatbots & SEO' },
        // Basic Twitter Card
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: 'Contact - Information request and quote | Arynae' },
        { name: 'twitter:description', content: 'Contact Arynae to discuss your digital projects. Request a free quote for our SEO services, SaaS creation, chatbots or No Code solutions.' },
        { name: 'twitter:image', content: 'https://arynae.com/img/arynae-social-card.png' },
        { name: 'viewport', content: 'width=device-width, initial-scale=1.0' }
      ],
      link: [
        { rel: 'canonical', href: 'https://arynae.com/en/contact' },
        { rel: 'alternate', hreflang: 'fr', href: 'https://arynae.com/contact' },
        { rel: 'alternate', hreflang: 'en', href: 'https://arynae.com/en/contact' }
      ],
      htmlAttrs: {
        lang: 'en'
      }
    })
    
    return {}
  },
  data() {
  return {
    formData: {
      nom: '',
      email: '',
      sujet: '',
      message: ''
    },
    puzzleImage: '/img/puzzle-captcha.jpg',
    puzzleVerified: false,
    successMessage: ''
  }
},
  methods: {
    navigateTo(code) {
      const routes = {
        'e1': 'https://cal.com/web-passion/rdv'
      };
      
      window.open(routes[code] || code, '_blank');
    },
    onVerificationChange(isValid) {
      this.puzzleVerified = isValid;
    },
    async submitForm() {
      // Vérifier si le puzzle a été complété
      if (!this.puzzleVerified) {
        alert('Please complete the verification puzzle');
        return;
      }

      try {
        // Récupérer la position de la pièce depuis le champ caché
        const puzzlePiecePosition = document.querySelector('[name="puzzle_piece_position"]').value;

        // Préparation des données pour l'envoi
        const payload = {
          nom: this.formData.nom,
          email: this.formData.email,
          sujet: this.formData.sujet,
          message: this.formData.message,
          remarque: 'atHUiopfgKuotG45sd',
          puzzle_piece_position: puzzlePiecePosition
        }

        // Envoi du formulaire
        const response = await axios.post('/d7NrautX1TjB8aG6xR9xjECh4.php', payload, {
          headers: {
            'Content-Type': 'application/json'
          }
        });

        // Réinitialisation du formulaire après envoi
        this.formData = {
          nom: '',
          email: '',
          sujet: '',
          message: ''
        };
        
        // Réinitialiser l'état du puzzle
        this.puzzleVerified = false;

        // Message de succès affiché sur la page (non bloquant)
        this.successMessage = (response.data && response.data.message) || 'Your message has been sent successfully!';

        // Redirection après le délai annoncé par le serveur (5 secondes réelles)
        if (response.data && response.data.redirect && response.data.redirect.url) {
          const delay = response.data.redirect.delay || 5000;
          setTimeout(() => {
            window.location.replace(response.data.redirect.url);
          }, delay);
        }
        
      } catch (error) {
        // Gestion des erreurs
        alert(error.response?.data?.message || 'An error occurred while sending the form');
        // Session puzzle consommée : charger un captcha neuf pour une nouvelle tentative
        this.$refs.puzzleCaptcha.reload();
      }
    }
}
}
</script>

<style scoped>
.form-success {
  background-color: #dcfce7;
  color: #166534;
  padding: 1rem;
  border-radius: 0.5rem;
  text-align: center;
}

.contact-page {
  font-family: Arial, sans-serif;
  color: #333;
}

/* Header Styles */
.header {
  background: linear-gradient(to right, #2563EB, #7C3AED);
  padding-bottom: 6rem;
  color: white;
}

.header-content {
  text-align: center;
  padding: 5rem 2rem 3rem;
}

.main-title {
  font-size: 2.75rem;
  margin-bottom: 1rem;
}

.subtitle {
  font-size: 1.25rem;
  max-width: 800px;
  margin: 0 auto;
}

/* Content Container */
.content-container {
  max-width: 1200px;
  margin: -3rem auto 4rem;
  padding: 0 2rem;
}

/* Card Styles */
.card {
  background: linear-gradient(to bottom, #ffffff, #f3f4f6);
  border-radius: 0.5rem;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  margin-bottom: 2rem;
  overflow: hidden;
}

.card-content {
  padding: 2rem;
}

.section-title {
  font-size: 1.75rem;
  margin-bottom: 1.5rem;
  color: #333;
  display: block;
  text-align: center;
}

.text-content {
  font-size: 1rem;
  line-height: 1.6;
  color: #555;
}

/* Call to Action */
.cta-container {
  text-align: center;
  margin: 2rem 0 1rem;
}

.cta-button {
  background: linear-gradient(to right, #2563EB, #7C3AED);
  color: white;
  font-size: 1.25rem;
  font-weight: bold;
  padding: 1rem 2.5rem;
  border: none;
  border-radius: 2rem;
  cursor: pointer;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  text-decoration: none;
  display: inline-block;
}

.cta-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.25);
}

/* Form Styles */
.contact-form {
  max-width: 800px;
  margin: 0 auto;
}

.form-group {
  margin-bottom: 1.5rem;
}

label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #333;
}

input, textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 0.5rem;
  font-size: 1rem;
  transition: border-color 0.3s ease;
}

input:focus, textarea:focus {
  outline: none;
  border-color: #2563EB;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
}

.form-note {
  font-size: 0.875rem;
  margin-bottom: 1.5rem;
  font-style: italic;
  color: #666;
  text-align: center;
}

.privacy-notice {
  font-size: 0.875rem;
  color: #666;
  margin-top: 2rem;
  text-align: center;
}

/* Responsive Adjustments */
@media (max-width: 768px) {
  .header-content {
    padding: 4rem 1rem 2rem;
  }
  
  .main-title {
    font-size: 2rem;
  }
  
  .content-container {
    padding: 0 1rem;
  }
  
  .card-content {
    padding: 1.5rem;
  }
  
  .cta-button {
    font-size: 1rem;
    padding: 0.75rem 1.5rem;
  }
}
</style>